import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Grid, Container } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilter,
  faArrowDownToLine,
} from '@fortawesome/pro-regular-svg-icons';
import {
  Button,
  Table,
  FilterType,
  TableFilter,
  useTable,
  useFilters,
  Flex,
} from 'portal-commons';

import { useAuthContext } from '@/contexts';
import EventsListDE from './EventsListDE';
import {
  fetchEventHistory,
  getAllEventTypes,
  downloadEventHistoryAsCsv,
} from '../apiServices';
import { MAX_EVENT_RECORDS } from '../constants';
import { Event } from '../types';
import { getAuthenticatedUserSuggestions } from '@/apis/services';
import { PageTitle } from '@/components';
import EventListingRow from '@/components/EventListingRow';
import {
  CLEAR_LOADING_MESSAGE,
  SET_LOADING_MESSAGE,
} from '@/shared_elements/actions';
import Loader from '@/shared_elements/containers/Loader';
import { toastFlashMessage } from '@/utils';
import { generateAndDownloadFile } from '@/utils/download';
import { globalGetService } from '@/utils/globalApiServices';
import { convertTimeWithTimezone } from '@/utils/time';
import { CombinedFilters, DEFAULT_ADDITIONAL_FILTERS } from '@/utils/types';

import '@/assets/styles/events-listing-module.scss';
import { constantizeAuthenticatedUser } from '@/utils/string';

const Today = new Date(new Date().setHours(0, 0, 0, 0));
const dateRangePresets = [
  {
    label: 'Today',
    start: Today,
    end: Today,
  },
  {
    label: 'Last 7 Days',
    start: moment(Today).subtract(7, 'days').toDate(),
    end: Today,
  },
  {
    label: 'Last 1 Month',
    start: moment(Today).subtract(1, 'months').toDate(),
    end: Today,
  },
  {
    label: 'Last 6 Months',
    start: moment(Today).subtract(6, 'months').toDate(),
    end: Today,
  },
];

const FILTER_CONFIGS = {
  campaignUid: {
    type: FilterType.Text,
    label: 'Campaign ID',
    placeholder: 'Enter campaign id',
    width: 160,
  },
  brandUid: {
    type: FilterType.Text,
    label: 'Brand ID',
    placeholder: 'Enter brand id',
    width: 160,
  },
  brandName: {
    type: FilterType.Text,
    label: 'Brand DBA Name',
    placeholder: 'Enter brand DBA name',
    suggestions: [],
    width: 255,
  },
  eventType: {
    type: FilterType.Dropdown,
    label: 'Event Type',
    placeholder: 'Select event type',
    options: [],
    width: 285,
  },
  dateRange: {
    type: FilterType.DateRange,
    label: 'Date Range',
    placeholder: 'Select date range',
    delayUpdate: false,
    dateRangePresets,
  },
  authenticatedUser: {
    type: FilterType.Text,
    label: 'User',
    placeholder: 'Enter User',
    suggestions: [],
    width: 255,
  },
  billable: {
    type: FilterType.Dropdown,
    label: 'Billable Events',
    placeholder: 'Select billable events',
    options: [
      { label: 'Billable Events', value: 'true' },
      { label: 'Non-Billable Events', value: 'false' },
    ],
  },
};

const headRows = [
  { label: 'EVENT', id: 'eventType', sortable: true },
  { label: 'CATEGORY', id: 'categoryType', sortable: true },
  { label: 'CAMPAIGN ID', id: 'campaignUid', sortable: true },
  { label: 'BRAND ID', id: 'brandUid', sortable: true },
  { label: 'DATE & TIME', id: 'createDate', sortable: true },
  { label: 'USER', id: 'authenticatedUser', sortable: false },
  { label: 'DESCRIPTION', id: 'description', sortable: false },
];

const fetchBrandSuggestions = async (query = {}): Promise<string[]> => {
  const response = await globalGetService('csp/brands/suggestions', {
    ...query,
    limit: 20,
  });
  return response?.ok
    ? response.data?.map(
        (d: { brandName: string; brandUid: string }) => d?.brandName
      )
    : [];
};

const FILTER_LOADERS = {
  suggestion: {
    brandName: {
      minLength: 2,
      load: (value: string) => fetchBrandSuggestions({ prefix: value }),
    },
    authenticatedUser: {
      minLength: 2,
      load: (value: string) => getAuthenticatedUserSuggestions(value),
    },
  },
};

const EventsList: React.FC = () => {
  const { profile } = useAuthContext();

  if (profile?.market === 'DE') {
    return <EventsListDE />;
  }

  const filter = useFilters({
    configs: FILTER_CONFIGS,
    loaders: FILTER_LOADERS,
  });
  const dispatch = useDispatch();
  const [filtersShown, setFiltersShown] = useState(false);

  const table = useTable<Event>(
    async () => {
      const query = getQuery();
      const eventData = await fetchEventHistory(query);

      return {
        data: eventData?.records || [],
        page: eventData?.page || 1,
        rowsPerPage: eventData?.recordsPerPage || 10,
        total: eventData?.totalRecords || 0,
      };
    },
    {
      cappingTotal: MAX_EVENT_RECORDS,
      deps: [filter.appliedFilters, filter.additionalFilters],
      defaultFilter: DEFAULT_ADDITIONAL_FILTERS,
    }
  );

  const setLoadingMessage = (message: string) => {
    dispatch({
      type: SET_LOADING_MESSAGE,
      payload: message,
    });
  };

  const clearLoadingMessage = () => {
    dispatch({
      type: CLEAR_LOADING_MESSAGE,
    });
  };

  const getQuery = (): CombinedFilters => {
    const authenticatedUser = constantizeAuthenticatedUser(
      filter.appliedFilters.authenticatedUser
    );
    return dateRangePreprocess({
      ...table.filter,
      ...filter.appliedFilters,
      ...filter.additionalFilters,
      ...(authenticatedUser ? { authenticatedUser } : {}),
    });
  };

  const fetchFilterOptions = async () => {
    const { setOptions } = filter;
    const eventTypes = await getAllEventTypes();
    setOptions(
      'eventType',
      eventTypes.map((item) => ({
        label: item.id,
        value: item.id,
      }))
    );
  };

  const dateRangePreprocess = (queries: CombinedFilters) => {
    const { dateRange, ...restQueries } = queries;
    const DATE_FORMAT = 'YYYY-MM-DD';
    if (dateRange) {
      const dates = (dateRange as string).replace(/\s/gi, '').split('-');
      if (dates.length === 2) {
        return {
          ...restQueries,
          startDate: convertTimeWithTimezone(dates[0], undefined, DATE_FORMAT),
          endDate: convertTimeWithTimezone(dates[1], undefined, DATE_FORMAT),
        };
      }
    }
    return restQueries;
  };

  const handleDownload = async () => {
    setLoadingMessage('We are generating the document');
    // the download query should be equal to the last successful list query
    const { page, ...restQuery } = getQuery();
    const response = await downloadEventHistoryAsCsv(restQuery);
    if (response) {
      const file = new File([response], 'event-history.csv');
      generateAndDownloadFile(file);
    } else {
      toastFlashMessage('Failed to download event history', 'error');
    }
    clearLoadingMessage();
  };

  useEffect(() => {
    document.title = 'The Campaign Registry - Events';
    fetchFilterOptions();
  }, []);

  return (
    <section
      className="events-listing-section"
      style={{ padding: '0px' }}
      data-testid="eventLists"
    >
      {table.pageLoading ? (
        <Loader />
      ) : (
        <Container maxWidth={false} style={{ padding: '0px' }}>
          <PageTitle
            note="This page can only show the latest 10,000 records. Contact support
              for details on your remaining records."
          >
            <span>
              {!table.pageLoading && table.total > 0
                ? `${table.total} Events`
                : 'Events'}
            </span>
          </PageTitle>
          <Flex
            sx={{ alignItems: 'center', gap: '10px', mb: '12px', mt: '4px' }}
          >
            <Button
              color="secondary"
              onClick={() => setFiltersShown(!filtersShown)}
              data-testid="tableAddFilterButton"
            >
              <FontAwesomeIcon icon={faFilter} />
              <span>{filtersShown ? 'Hide' : 'Show'} Filters</span>
            </Button>
            <Button
              variant="outline"
              color="secondary"
              onClick={handleDownload}
              data-testid="tableDownloadButton"
            >
              <FontAwesomeIcon icon={faArrowDownToLine} />
              Download
            </Button>
          </Flex>
          {filtersShown && (
            <Grid container style={{ marginBottom: '12px' }}>
              <TableFilter
                configs={filter.configs}
                candidateValues={filter.candidateFilters}
                appliedValues={filter.appliedFilters}
                onCandidateValuesChange={filter.handleEdit}
                onAppliedValuesChange={filter.handleApply}
                data-testid="eventListTableFilter"
              />
            </Grid>
          )}
          <Grid container justifyContent="center" spacing={0}>
            <Table
              testId="eventListingTable"
              className="events-listing-table"
              headRows={headRows}
              emptyState="no events to view"
              disableHover
              loading={table.loading}
              tableData={table.tableData.map((record, idx) => {
                return (
                  <EventListingRow
                    key={`record_${idx}`}
                    event={record}
                    campaignLink={true}
                    brandLink={true}
                  />
                );
              })}
              handleChangePage={table.handleChangePage}
              createSortHandler={table.handleSorting}
              filter={table.filter}
              pagination={table.pagination}
            />
          </Grid>
        </Container>
      )}
    </section>
  );
};

export default EventsList;
